// API Build của data-api (SAA-2026-007): xem trước · duyệt · áp dụng · version · manifest. Spec dùng id có tiền tố; manifest
// (thứ runtime đọc) ở không gian uuid — cùng không gian với `values` của record (Dynamic Data).
import { IsoDateTime, list, success, Uuid } from "@secretaa/http-contract";
import { z } from "zod";
import { FieldType, I18nLabel } from "../dynamic-data.ts";
import { BlueprintSpec } from "./spec.ts";

export const Risk = z.enum(["SAFE", "REVIEW", "DANGEROUS", "CRITICAL"]);
export const DiffOp = z
  .object({
    op: z.string(),
    entity: z.enum(["collection", "field", "option", "view", "policy", "scopeType"]),
    id: z.string(),
    parentId: z.string().optional(),
    from: z.unknown().optional(),
    to: z.unknown().optional(),
    risk: Risk,
    dataTouch: z.boolean(),
    /** Số record bị ảnh hưởng — truy vấn thật (ADR-001 mục 4); null khi thao tác không chạm record. */
    records: z.number().int().nullable(),
  })
  .strict();

const Permission = z.enum(["read", "create", "update", "retire"]);
export const ManifestField = z
  .object({
    id: Uuid,
    key: z.string(),
    label: I18nLabel,
    type: FieldType,
    required: z.boolean(),
    indexed: z.boolean(),
    status: z.enum(["active", "retired"]),
    config: z.union([
      z.object({}).strict(),
      z.object({ currency: z.string(), minorUnitScale: z.number().int() }).strict(),
      z.object({ options: z.array(z.object({ id: Uuid, key: z.string(), label: I18nLabel, status: z.enum(["active", "retired"]) }).strict()) }).strict(),
      z.object({ targetCollectionId: Uuid, cardinality: z.literal("one") }).strict(),
    ]),
    position: z.number().int(),
  })
  .strict();
export const Manifest = z
  .object({
    schemaVersion: z.literal(1),
    collections: z.array(
      z
        .object({
          id: Uuid,
          key: z.string(),
          label: I18nLabel,
          status: z.enum(["active", "retired"]),
          policy: z.object({ viewer: z.array(Permission), editor: z.array(Permission), manager: z.array(Permission) }).strict(),
          fields: z.array(ManifestField),
        })
        .strict(),
    ),
    views: z.array(
      z
        .object({
          id: Uuid,
          collectionId: Uuid,
          key: z.string(),
          label: I18nLabel,
          kind: z.enum(["table", "form", "detail"]),
          fields: z.array(Uuid),
          filter: z.unknown(),
          sort: z.object({ field: z.union([Uuid, z.literal("createdAt")]), dir: z.enum(["asc", "desc"]) }).strict(),
        })
        .strict(),
    ),
    scopeTypes: z.array(z.object({ id: Uuid, key: z.string(), label: I18nLabel }).strict()),
  })
  .strict();

export const BlueprintState = z.object({ activeVersion: z.number().int(), spec: BlueprintSpec, manifest: Manifest.nullable(), manifestHash: z.string().nullable() }).strict();
export const BlueprintResponse = success(BlueprintState);

const Hash = z.string().regex(/^[0-9a-f]{64}$/);
export const PreviewBlueprintBody = z.object({ spec: BlueprintSpec }).strict();
export const BlueprintPlan = z
  .object({ baseVersion: z.number().int(), ops: z.array(DiffOp), risk: Risk, dataTouchRecords: z.number().int(), diffHash: Hash, targetSpecHash: Hash })
  .strict();
export const BlueprintPlanResponse = success(BlueprintPlan);

export const ApproveBlueprintBody = z
  .object({ spec: BlueprintSpec, baseVersion: z.number().int().min(0), diffHash: Hash, confirm: z.boolean().optional(), confirmProjectKey: z.string().max(20).optional() })
  .strict();
export const BlueprintApprovalResponse = success(z.object({ approvalId: Uuid, expiresAt: IsoDateTime, risk: Risk }).strict());

/** `If-Match: W/"<baseVersion>"` bắt buộc (version Blueprint đang active mà diff dựa trên). */
export const ApplyBlueprintBody = z.object({ spec: BlueprintSpec, diffHash: Hash, approvalId: Uuid.optional() }).strict();
export const BlueprintApplyResponse = success(
  z.object({ version: z.number().int(), risk: Risk, manifestHash: Hash, idMap: z.record(z.string(), z.string()), ops: z.array(DiffOp) }).strict(),
);

export const BlueprintVersionSummary = z
  .object({ version: z.number().int(), baseVersion: z.number().int(), risk: Risk, status: z.enum(["pending", "active", "superseded"]), diffHash: Hash, manifestHash: Hash, ops: z.number().int(), createdBy: Uuid, createdAt: IsoDateTime, activatedAt: IsoDateTime.nullable() })
  .strict();
export const BlueprintVersionsResponse = list(BlueprintVersionSummary);
export const ListBlueprintVersionsQuery = z.object({ before: z.coerce.number().int().positive().optional(), limit: z.coerce.number().int().min(1).max(100).default(20) }).strict();
export const BlueprintVersionDetail = BlueprintVersionSummary.extend({ spec: BlueprintSpec, manifest: Manifest, diff: z.array(DiffOp) }).strict();
export const BlueprintVersionResponse = success(BlueprintVersionDetail);

export const BlueprintTemplate = z.object({ key: z.string(), label: I18nLabel, description: I18nLabel, spec: BlueprintSpec }).strict();
export const BlueprintTemplatesResponse = success(z.array(BlueprintTemplate));
